import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type MatDialog } from '@angular/material/dialog';
import { type ActivatedRoute } from '@angular/router';
import { COUNTRY_LIST, DEFAULT_COUNTRY, PhoneNumberClass } from '@core';
import { type ClientUser, UserRolesTypes, UserStatus } from '@models';
import { type Store } from '@ngrx/store';
import { AuthSelectors, ClientUsersActions, ClientUsersSelectors, type UserState } from '@ngrx-store';
import { ConfirmationDialogComponent } from '@ui';
import { type Observable } from 'rxjs';

export abstract class UserDetailsClass extends PhoneNumberClass {
  user!: ClientUser;
  editor$: Observable<UserState> | undefined;

  resendInvitationLoading$: Observable<boolean>;
  destroyRef = inject(DestroyRef);

  protected readonly UserRolesTypes = UserRolesTypes;
  protected readonly UserStatus = UserStatus;
  protected readonly COUNTRY_LIST = COUNTRY_LIST;
  protected readonly DEFAULT_COUNTRY = DEFAULT_COUNTRY;

  protected constructor(public route: ActivatedRoute, public store: Store, public dialog: MatDialog) {
    super();
    this.store
      .select(ClientUsersSelectors.getCompanyUserById(this.route.snapshot.params['id']))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (user) {
          this.user = user;
          const selectedCountry = COUNTRY_LIST.find(country => country.code === user.phone.countryCode);

          if (selectedCountry) {
            this.phoneMask.set(selectedCountry.mask);
            this.phoneMaskExpression.set(selectedCountry.maskExpression);
          }
        }
      });
    this.editor$ = this.store.select(AuthSelectors.selectAuthState);
    this.resendInvitationLoading$ = this.store.select(ClientUsersSelectors.resendInvitationIsLoading);
  }

  openConfirmationDialog({
    userId,
    title,
    description,
    data,
  }: {
    userId: string;
    title: string;
    description: string;
    data: Partial<ClientUser>;
  }) {
    ConfirmationDialogComponent.open(this.dialog, {
      title,
      description,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmation => {
        if (confirmation) {
          this.store.dispatch(
            ClientUsersActions.updateUserClient({
              clientUserId: userId,
              clientUser: data,
            }),
          );
        }
      });
  }

  archive(user: ClientUser) {
    this.openConfirmationDialog({
      userId: user.id,
      title: 'Archive',
      description: `Do you really want to archive ${user.firstName} ${user.lastName} ?`,
      data: { isArchived: true },
    });
  }

  restore(user: ClientUser) {
    this.openConfirmationDialog({
      userId: user.id,
      title: 'Restore',
      description: `Do you really want to restore ${user.firstName} ${user.lastName} ?`,
      data: { isArchived: false },
    });
  }

  resendInvitation(user: ClientUser) {
    this.store.dispatch(ClientUsersActions.resendUserInvitation({ clientUser: user }));
  }
}
